import { MouseEvent, useState } from 'react';

import { AppIcon, AppIcons } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { OwnershipLevel } from '@backrs_ui/grow-ui/src/utils/determineOwnership';

import { Box, Chip, SxProps, Theme } from '@mui/material';

import { OwnershipLevelsModal } from './OwnershipLevelsModal';

const STRINGS = appStrings.modules.survey.output.blueprintSection.ownership;

export const OWNERSHIP_DISPLAY: { level: OwnershipLevel; label: string; icon: AppIcons }[] = [
  { level: 'critical', label: STRINGS.levels.critical, icon: 'AnnotationAlert' },
  { level: 'important', label: STRINGS.levels.important, icon: 'AnnotationCheck' },
  { level: 'less_important', label: STRINGS.levels.lessImportant, icon: 'AnnotationDots' },
];

type OwnershipCardProps = Readonly<{
  ownershipLevel: OwnershipLevel | null;
  /** When false, pills do not open the modal on click. Defaults to true. */
  interactive?: boolean;
}>;

export function OwnershipCard({ ownershipLevel, interactive = true }: OwnershipCardProps) {
  const [modalLevel, setModalLevel] = useState<OwnershipLevel | null>(null);

  if (!ownershipLevel) return null;

  const handleClick = (e: MouseEvent, level: OwnershipLevel) => {
    if (!interactive) return;

    e.stopPropagation();
    setModalLevel(level);
  };

  return (
    <>
      <Box sx={containerSx}>
        {OWNERSHIP_DISPLAY.map(({ level, label, icon }) => {
          const isSelected = level === ownershipLevel;

          return (
            <Chip
              key={level}
              id={`ownership-pill-${level.toLowerCase().replace(/ /g, '-')}`}
              label={label}
              size="md"
              icon={
                <AppIcon
                  iconName={icon}
                  diameter="12px"
                  color={isSelected ? 'Brand/600' : 'Colors/Foreground/fg-quaternary'}
                />
              }
              sx={pillSx(isSelected, interactive)}
              variant="badge"
              type="leading"
              onClick={interactive ? (e) => handleClick(e, level) : undefined}
            />
          );
        })}
      </Box>

      <OwnershipLevelsModal
        open={modalLevel !== null}
        onClose={() => setModalLevel(null)}
        ownershipLevel={modalLevel ?? ownershipLevel}
      />
    </>
  );
}

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: backrs.spacing['spacing-sm'],
});

const pillSx =
  (isSelected: boolean, interactive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    border: `1px solid ${backrs.colors[isSelected ? 'Brand/300' : 'Colors/Border/border-secondary']}`,
    backgroundColor: isSelected ? backrs.colors['Brand/50'] : 'transparent',
    color: backrs.colors['Colors/Foreground/fg-primary'],
    cursor: interactive ? 'pointer' : 'default',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    // MUI Chip applies a focus ring after click; suppress it.
    '&:focus, &:focus-visible': {
      outline: 'none',
      boxShadow: 'none',
    },
    ...(interactive && {
      '&:hover': {
        backgroundColor: isSelected ? backrs.colors['Brand/100'] : backrs.colors['Gray/50'],
        borderColor: backrs.colors[isSelected ? 'Brand/400' : 'Gray/300'],
      },
    }),
  });
